import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../util/notificationsApi';
import { logger } from '../../util/logger';
import { isSafeHref } from '../../util/safeHtml';
import { Badge, Button, EmptyState, Sheet, Skeleton, SkeletonText } from '../ui';
import { useT } from '../../i18n/I18nContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faTrash,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';

// Matches Tailwind's ``sm`` breakpoint + the same constant ReaderControl
// uses for its bottom-sheet/dropdown swap, so notifications and TTS share
// one mental model for "is this a phone-shaped viewport".
const MOBILE_BREAKPOINT_PX = 640;

// Visual tone per notification kind. Maps to the ``Badge`` component's
// ``tone`` prop so kinds stay categorically distinct without us
// re-stating Tailwind colour classes per row.
const KIND_TONE = {
  'collab.invite': 'primary',
  'collab.comment': 'accent',
  'goal.reached': 'success',
  publication: 'primary',
  system: 'neutral',
  report: 'warning',
};

// Available filters for the tab strip. ``id`` keys the active state,
// ``labelKey`` resolves through ``useT`` so the strip i18n's cleanly,
// and ``predicate`` is what ``items`` is filtered through. Easy to
// extend with a per-kind filter later (e.g. "Mentions").
const FILTERS = [
  { id: 'all', labelKey: 'notifications.filter.all', predicate: () => true },
  {
    id: 'unread',
    labelKey: 'notifications.filter.unread',
    predicate: (n) => !n.read,
  },
];

export default function NotificationsPanel({ onClose, inline = false }) {
  const t = useT();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState(null);
  const [filterId, setFilterId] = useState('all');
  // ``inline`` callers embed the panel inside another scroll
  // container (e.g. the mobile navbar tabs) and never need the
  // mobile-specific Sheet wrapper — they're already rendered
  // inside their host's chrome. We still keep the viewport
  // detection for desktop callers that share this component.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia?.(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches,
  );
  const panelRef = useRef(null);

  // Track viewport so a rotated phone or resized window flips
  // between bottom-sheet and anchored-dropdown without a remount.
  // Mirrors the pattern ReaderControl uses for its TTS popover.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await listNotifications();
      setItems(data.notifications ?? []);
      setUnread(data.unread_count ?? 0);
    } catch (err) {
      logger.warn('notifications_load_failed', err?.message);
      setError(t('notifications.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Background refresh — 30 s cadence. The navbar bell already
  // polls the unread count every 60 s; this faster tick is panel-
  // local so it's only paid for while the panel is open.
  useEffect(() => {
    const id = window.setInterval(load, 30000);
    return () => window.clearInterval(id);
  }, [load]);

  // Desktop dropdown-style dismissal: click outside the card closes it.
  // Skipped on mobile because the Sheet handles its own backdrop /
  // Escape / focus-trap, and adding a second listener would race the
  // Sheet's close handler.
  useEffect(() => {
    if (isMobile) return undefined;
    const onClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose, isMobile]);

  const filter = FILTERS.find((f) => f.id === filterId) ?? FILTERS[0];
  const visible = useMemo(() => items.filter(filter.predicate), [items, filter]);

  // Tell the navbar bell badge to re-poll its count from the source
  // of truth. Panel and navbar own separate state — without this
  // ping the navbar pip stayed stale until its 60 s timer ticked,
  // even though the user had just hit "mark all read".
  const pingBadge = () => {
    window.dispatchEvent(new Event('wo:badges:refresh'));
  };

  async function handleMarkRead(item) {
    if (item.read) return;
    // Optimistic — flip locally first, roll back if the API rejects.
    setItems((prev) =>
      prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)),
    );
    setUnread((count) => Math.max(count - 1, 0));
    try {
      await markNotificationRead(item.id, true);
      pingBadge();
    } catch (err) {
      logger.warn('mark_read_failed', err?.message);
      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, read: false } : n)),
      );
      setUnread((count) => count + 1);
    }
  }

  async function handleMarkAll() {
    const previous = items;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
      pingBadge();
    } catch (err) {
      logger.warn('mark_all_failed', err?.message);
      setItems(previous);
      setUnread(previous.filter((n) => !n.read).length);
    }
  }

  async function handleDelete(item) {
    const previous = items;
    setItems((prev) => prev.filter((n) => n.id !== item.id));
    if (!item.read) setUnread((count) => Math.max(count - 1, 0));
    try {
      await deleteNotification(item.id);
      if (!item.read) pingBadge();
    } catch (err) {
      logger.warn('delete_notification_failed', err?.message);
      setItems(previous);
      if (!item.read) setUnread((count) => count + 1);
    }
  }

  // Whole-row click: mark as read + follow the href if it's safe and
  // routes inside the SPA. External links use ``window.open`` so the
  // panel stays put. Without this affordance writers had to scan for
  // the small "Open" link instead of just clicking the row.
  function handleRowClick(item) {
    handleMarkRead(item);
    if (!isSafeHref(item.href)) return;
    if (item.href.startsWith('/')) {
      navigate(item.href);
      onClose?.();
    } else {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    }
  }

  // Compact sub-header that lives below the Sheet's own title row
  // (mobile) or as the card's full header (desktop). Holds the
  // unread-count summary + Refresh / Mark-all-read actions.
  //
  // Padding + border weights tuned to mirror the shared Sheet
  // header (px-4 sm:px-5, py-3 sm:py-3.5, border-border/60) so
  // the anchored desktop card reads as one chrome family with
  // every Sheet-based modal across the app. h2 size lifted to
  // text-base / sm:text-lg to match Sheet's title hierarchy.
  const subHeader = (
    <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-3.5 border-b border-border/60">
      <div className="min-w-0">
        {/* On desktop this is the only header — render the title;
            on mobile the Sheet draws its own title row, so we just
            surface the unread count. */}
        {!isMobile ? (
          <h2 className="text-base sm:text-lg font-semibold text-text truncate">
            {t('nav.notifications')}
          </h2>
        ) : null}
        <p className="text-[11px] text-secondText mt-0.5">
          {unread > 0
            ? t('notifications.unread_count', { count: String(unread) })
            : t('notifications.all_caught_up')}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="ghost" onClick={load}>
          {t('notifications.refresh')}
        </Button>
        {unread > 0 ? (
          <Button size="sm" variant="outline" onClick={handleMarkAll}>
            <FontAwesomeIcon icon={faCheck} className="mr-1.5 text-[10px]" />
            {t('notifications.mark_all_read')}
          </Button>
        ) : null}
      </div>
    </div>
  );

  const filterTabs = (
    // Filter tabs — All / Unread. Compact pill row so the panel
    // stays narrow and the filter is one tap away instead of
    // buried in a menu. Horizontal padding matches the header
    // (px-4 sm:px-5) so the tabs hang flush under the title.
    <div className="flex items-center gap-1 px-4 sm:px-5 pt-3 shrink-0">
      {FILTERS.map((f) => {
        const active = f.id === filterId;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilterId(f.id)}
            className={`text-xs font-semibold px-3 py-1 rounded-full transition ${
              active
                ? 'bg-primaryMuted text-primary'
                : 'text-secondText hover:text-text hover:bg-surfaceAlt'
            }`}
          >
            {t(f.labelKey)}
            {f.id === 'unread' && unread > 0 ? (
              <span
                className={`ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                  active ? 'bg-primary text-white' : 'bg-secondText/20 text-secondText'
                }`}
              >
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  const listBody = (
    <div className="flex-1 overflow-y-auto custom-scrollbar mt-1">
      {loading ? (
        <div className="space-y-3 p-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border p-3">
              <Skeleton className="h-3 w-1/3 mb-2" />
              <SkeletonText lines={2} />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="p-4 text-sm text-danger">{error}</div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<FontAwesomeIcon icon={faBell} className="text-2xl" />}
          title={
            filterId === 'unread'
              ? t('notifications.empty.unread.title')
              : t('notifications.empty.title')
          }
          description={
            filterId === 'unread'
              ? t('notifications.empty.unread.description')
              : t('notifications.empty.description')
          }
        />
      ) : (
        <ul className="divide-y divide-border">
          {visible.map((n) => (
            <li key={n.id} className="group relative">
              {/* Inner clickable surface is a div with role="button"
                  so we keep the <li> semantically a list item
                  (lint: no-noninteractive-element-to-interactive-
                  role); the delete affordance lives outside it
                  (absolute, right edge) so buttons don't nest. */}
              <div
                className={`flex gap-3 pl-4 sm:pl-5 pr-12 sm:pr-12 py-3 text-sm transition cursor-pointer ${
                  n.read
                    ? 'bg-transparent hover:bg-surfaceAlt'
                    : 'bg-primaryMuted/40 hover:bg-primaryMuted/60'
                }`}
                onClick={() => handleRowClick(n)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(n);
                  }
                }}
                tabIndex={0}
                role="button"
              >
              {/* Unread-pip + kind tag stacked on the left so the
                  title gets full row width to wrap into. */}
              <div className="flex flex-col items-start gap-1.5 mt-0.5">
                {!n.read ? (
                  <span
                    aria-label={t('notifications.unread')}
                    className="w-2 h-2 rounded-full bg-primary"
                  />
                ) : (
                  <span className="w-2 h-2" aria-hidden="true" />
                )}
                <Badge tone={KIND_TONE[n.kind] ?? 'neutral'}>
                  {n.kind.split('.')[0]}
                </Badge>
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`${
                    n.read ? 'font-medium text-text' : 'font-semibold text-text'
                  } break-words`}
                >
                  {n.title}
                </p>
                {n.body ? (
                  <p className="text-secondText mt-0.5 whitespace-pre-wrap break-words">
                    {n.body}
                  </p>
                ) : null}
              </div>
              </div>
              {/* Delete affordance sits OUTSIDE the role=button
                  clickable surface (absolute-positioned on the
                  right edge) so it doesn't nest a button inside
                  another button. Hover-revealed on desktop; on
                  touch we keep it visible at 60% opacity since
                  there's no hover state. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(n);
                }}
                aria-label={t('notifications.delete')}
                title={t('notifications.delete')}
                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 inline-flex w-8 h-8 sm:w-7 sm:h-7 items-center justify-center rounded-full text-secondText hover:bg-danger/10 hover:text-danger transition bg-surface/80"
              >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // --- Inline: embedded host (e.g. mobile sidebar tabs) ------------
  // Skips the Sheet wrapper entirely so the host owns the chrome
  // (header, backdrop, dismiss). The body fills the host with a
  // flex column so the list scrolls inside the host container.
  if (inline) {
    return (
      <div className="flex flex-col h-full min-h-0">
        {subHeader}
        {filterTabs}
        {listBody}
      </div>
    );
  }

  // --- Mobile: native bottom sheet ---------------------------------
  // Uses the shared <Sheet variant="bottom"> primitive so notifications
  // share their entrance animation, focus trap, backdrop, body-scroll
  // lock and Escape-to-close with every other mobile drawer in the app
  // (ImportSheet, ModuleDrawer, the message Modal). Replaces the prior
  // ``fixed inset-x-2 top-[68px] bottom-2`` overlay that floated above
  // the page and never quite belonged.
  if (isMobile) {
    return (
      <Sheet
        open
        onClose={onClose}
        title={t('nav.notifications')}
        variant="bottom"
        size="lg"
      >
        {subHeader}
        {filterTabs}
        {listBody}
      </Sheet>
    );
  }

  // --- Desktop: anchored top-right card ----------------------------
  // Anchored near the bell — popover semantics, not a modal sheet.
  // Chrome matches the Sheet/browse-modal vocabulary (rounded-2xl,
  // bg-surface, border, shadow-xl) so all dialog surfaces across
  // the app share one design language. The ``app-surface`` utility
  // is intentionally omitted — its baked-in rounded/border/shadow
  // tokens conflict with the unified chrome the rest of the app
  // moved to in the modal-consistency pass.
  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('nav.notifications')}
      className="fixed right-4 top-[80px] z-50 w-[24rem] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-hidden flex flex-col rounded-2xl border border-border bg-surface shadow-xl"
    >
      {subHeader}
      {filterTabs}
      {listBody}
    </div>
  );
}
